<template>
    <div class="audio-settings">
        <!-- Search and header section -->
        <div class="header-section">
            <div class="title-area">
                <v-icon color="#0d2c6d" size="28" class="title-icon">mdi-microphone-settings</v-icon>
                <h2 class="section-title">הקלטות מערכת</h2>
            </div>
            <div class="header-actions">
                <v-chip small outlined color="#0d2c6d" class="records-count" v-if="systemRecords.length">
                    <v-icon small left>mdi-music-note</v-icon>
                    {{ systemRecords.length }} הקלטות
                </v-chip>
                <div class="search-container">
                    <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" placeholder="חיפוש לפי קוד, תוכן..."
                        outlined dense hide-details class="search-field" clearable></v-text-field>
                </div>
            </div>
        </div>

        <!-- Main content -->
        <div class="table-container">
            <v-data-table :headers="headers" :items="systemRecords" :search="search" :footer-props="{
                'items-per-page-options': [50, 100, 500],
                'items-per-page-text': 'שורות בעמוד:'
            }" class="audio-table" :loading="progressShow" loading-text="טוען נתונים...">

                <template v-if="$vuetify.breakpoint.mdAndUp" v-for="header in headers"
                    v-slot:[`header.${header.value}`]="{ header }">
                    <span class="table-header-text">{{ header.text }}</span>
                </template>

                <!-- Table rows -->
                <template v-slot:item="{ item, index }">
                    <tr class="table-row" :class="{ 'row-playing': item.playingLanguage }">
                        <!-- File name -->
                        <td class="text-center td-filename">
                            <v-chip small label outlined color="#546e8a" class="filename-chip">
                                {{ item.fileName }}
                            </v-chip>
                        </td>

                        <!-- Hebrew -->
                        <td class="text-center td-audio">
                            <v-btn :icon="item.playingLanguage !== 'he'" :fab="item.playingLanguage === 'he'" x-small
                                :color="item.playingLanguage === 'he' ? '#e53935' : '#0d2c6d'"
                                :dark="item.playingLanguage === 'he'" :outlined="item.playingLanguage !== 'he'"
                                @click="item.playingLanguage === 'he' ? stop(item) : play(item, 'he')" class="play-btn"
                                elevation="0">
                                <v-icon small>{{ item.playingLanguage === 'he' ? 'mdi-stop' : 'mdi-play' }}</v-icon>
                            </v-btn>
                        </td>

                        <!-- English -->
                        <td class="text-center td-audio">
                            <v-btn :icon="item.playingLanguage !== 'en'" :fab="item.playingLanguage === 'en'" x-small
                                :color="item.playingLanguage === 'en' ? '#e53935' : '#0d2c6d'"
                                :dark="item.playingLanguage === 'en'" :outlined="item.playingLanguage !== 'en'"
                                @click="item.playingLanguage === 'en' ? stop(item) : play(item, 'en')" class="play-btn"
                                elevation="0">
                                <v-icon small>{{ item.playingLanguage === 'en' ? 'mdi-stop' : 'mdi-play' }}</v-icon>
                            </v-btn>
                        </td>

                        <!-- Yiddish Israeli -->
                        <td class="text-center td-audio">
                            <v-btn :icon="item.playingLanguage !== 'yi_il'" :fab="item.playingLanguage === 'yi_il'"
                                x-small :color="item.playingLanguage === 'yi_il' ? '#e53935' : '#0d2c6d'"
                                :dark="item.playingLanguage === 'yi_il'" :outlined="item.playingLanguage !== 'yi_il'"
                                @click="item.playingLanguage === 'yi_il' ? stop(item) : play(item, 'yi_il')"
                                class="play-btn" elevation="0">
                                <v-icon small>{{ item.playingLanguage === 'yi_il' ? 'mdi-stop' : 'mdi-play' }}</v-icon>
                            </v-btn>
                        </td>

                        <!-- Yiddish American -->
                        <td class="text-center td-audio">
                            <v-btn :icon="item.playingLanguage !== 'yi_us'" :fab="item.playingLanguage === 'yi_us'"
                                x-small :color="item.playingLanguage === 'yi_us' ? '#e53935' : '#0d2c6d'"
                                :dark="item.playingLanguage === 'yi_us'" :outlined="item.playingLanguage !== 'yi_us'"
                                @click="item.playingLanguage === 'yi_us' ? stop(item) : play(item, 'yi_us')"
                                class="play-btn" elevation="0">
                                <v-icon small>{{ item.playingLanguage === 'yi_us' ? 'mdi-stop' : 'mdi-play' }}</v-icon>
                            </v-btn>
                        </td>

                        <!-- Custom -->
                        <td class="text-center td-custom">
                            <div class="custom-controls">
                                <v-btn :icon="item.playingLanguage !== 'custom'"
                                    :fab="item.playingLanguage === 'custom'" x-small
                                    :color="item.playingLanguage === 'custom' ? '#e53935' : '#0d2c6d'"
                                    :dark="item.playingLanguage === 'custom'"
                                    :outlined="item.playingLanguage !== 'custom'"
                                    @click="item.playingLanguage === 'custom' ? stop(item) : play(item, null)"
                                    class="play-btn" elevation="0">
                                    <v-icon small>{{ item.playingLanguage === 'custom' ? 'mdi-stop' : 'mdi-play'
                                    }}</v-icon>
                                </v-btn>

                                <v-tooltip bottom content-class="action-tooltip" open-delay="300">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn icon x-small color="#43a047" v-bind="attrs" v-on="on"
                                            @click="triggerFileInput(index)" class="action-btn">
                                            <v-icon small>mdi-cloud-upload-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>העלאת הקלטה מותאמת</span>
                                </v-tooltip>

                                <input type="file" :id="'fileInput' + index" ref="fileInputs"
                                    @change="uploadFile(item, $event)" style="display: none" accept="audio/*" />

                                <v-tooltip v-if="item.costumeExist" bottom content-class="action-tooltip"
                                    open-delay="300">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn icon x-small color="#e53935" v-bind="attrs" v-on="on"
                                            @click="confirmDelete(item)" class="action-btn">
                                            <v-icon small>mdi-delete-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>מחיקת הקלטה מותאמת</span>
                                </v-tooltip>
                            </div>
                        </td>

                        <!-- Content -->
                        <td class="text-center td-content">
                            <v-tooltip v-if="item.fileContent" bottom max-width="420" content-class="content-tooltip"
                                open-delay="200">
                                <template v-slot:activator="{ on, attrs }">
                                    <div class="file-content-wrapper" v-bind="attrs" v-on="on">
                                        <span class="file-content-text">{{ item.fileContent }}</span>
                                        <v-icon x-small class="expand-icon">mdi-information-outline</v-icon>
                                    </div>
                                </template>
                                <div class="tooltip-content">{{ item.fileContent }}</div>
                            </v-tooltip>
                            <span v-else class="no-content">—</span>
                        </td>
                    </tr>
                </template>

                <!-- Empty state -->
                <template v-slot:no-data>
                    <div class="empty-state">
                        <div class="empty-icon-wrapper">
                            <v-icon size="48" color="#b0bec5">mdi-microphone-off</v-icon>
                        </div>
                        <p class="empty-title">אין הקלטות מערכת זמינות</p>
                        <p class="empty-subtitle">נסה לרענן את הדף</p>
                        <v-btn color="#0d2c6d" dark rounded small @click="refreshDetails" class="mt-3" elevation="1">
                            <v-icon left small>mdi-refresh</v-icon>
                            רענן
                        </v-btn>
                    </div>
                </template>
            </v-data-table>
        </div>

        <!-- Loading overlay -->
        <v-overlay :value="overlay" :opacity="0.7">
            <v-card class="loading-card" elevation="12">
                <v-progress-circular indeterminate size="48" width="3" color="#0d2c6d"
                    class="mb-3"></v-progress-circular>
                <span class="loading-text">{{ loadingText }}</span>
            </v-card>
        </v-overlay>

        <!-- Delete confirmation dialog -->
        <v-dialog v-model="deleteDialog" max-width="380">
            <v-card class="delete-card">
                <v-card-title class="delete-title">
                    <v-icon color="#e53935" class="ml-2">mdi-delete-alert</v-icon>
                    מחיקת הקלטה
                </v-card-title>
                <v-card-text class="delete-text">
                    האם אתה בטוח שברצונך למחוק את ההקלטה המותאמת אישית?
                    <br>
                    <strong>פעולה זו אינה ניתנת לביטול.</strong>
                </v-card-text>
                <v-card-actions class="delete-actions">
                    <v-btn text color="grey darken-1" @click="deleteDialog = false" class="action-cancel">
                        ביטול
                    </v-btn>
                    <v-btn color="#e53935" dark rounded small @click="deleteConfirmed" elevation="0">
                        <v-icon left small>mdi-delete</v-icon>
                        מחק
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Snackbar for notifications -->
        <v-snackbar v-model="snackbar" :color="snackbarColorBt" :timeout="4000" bottom right rounded="lg">
            <div class="snackbar-content">
                <v-icon dark small class="ml-2">{{ snackbarColorBt === 'red' ? 'mdi-alert-circle' : 'mdi-check-circle'
                }}</v-icon>
                {{ snacbarText }}
            </div>
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar = false" small>
                    סגור
                </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import ApiServices from '@/services/api.service'
import Auth from '@/services/auth.service.js'
import axios from 'axios'

export default {
    name: 'AudioSettings',
    data: () => ({
        snackbar: false,
        snackbarColorBt: "success",
        snacbarText: "",
        lineNumber: "",
        progressShow: false,
        deleteDialog: false,
        itemToDelete: null,
        search: "",
        systemRecords: [],
        overlay: false,
        loadingText: "טוען...",
        headers: [
            { text: 'קוד הודעה', align: 'center', value: 'fileName', width: '14%' },
            { text: 'עברית', value: 'he', align: 'center', width: '8%' },
            { text: 'אנגלית', value: 'en', align: 'center', width: '8%' },
            { text: 'אידיש ישראלי', value: 'yi_il', align: 'center', width: '9%' },
            { text: 'אידיש אמריקאי', value: 'yi_us', align: 'center', width: '9%' },
            { text: 'מותאם אישית', value: 'costume', align: 'center', width: '14%' },
            { text: 'תוכן ההקלטה', value: 'fileContent', align: 'center' },
        ],
    }),
    methods: {
        async getSystemRecords() {
            try {
                this.progressShow = true;
                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + `/settings/get_system_records`;
                const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
                const jsonObject = await res.json();
                this.progressShow = false;

                if (res.status >= 400) {
                    this.showSnackBar("שגיאה בקבלת נתונים", "error");
                } else if (res.status === 200) {
                    this.systemRecords = jsonObject.map(record => ({
                        ...record,
                        playingLanguage: null
                    }));
                }
            } catch (error) {
                this.progressShow = false;
                this.showSnackBar("שגיאה בטעינת נתונים: " + error, "error");
            }
        },

        async setAudio(record, language) {
            try {
                this.loadingText = "טוען הקלטה...";
                this.overlay = true;

                let token = localStorage.getItem("token");
                let api;
                const firstChar = record.fileName.charAt(0);

                if (record.costumeExist && !language) {
                    api = process.env.VUE_APP_BASE_URL + `/recordings/costumFile/${this.lineNumber}/${firstChar}/` + record.fileName;
                } else {
                    api = process.env.VUE_APP_BASE_URL + `/recordings/systemFile/${language}/${firstChar}/` + record.fileName;
                }

                const res = await fetch(api, ApiServices.requestOptions("GET", "", token));

                if (res.status >= 400) {
                    this.showSnackBar("שגיאה בטעינת ההקלטה", "error");
                    record.record_link = "";
                    record.record_audio = "";
                    this.overlay = false;
                    return false;
                } else if (res.status === 200) {
                    const jsonObject = await res.blob();
                    const url = URL.createObjectURL(jsonObject);
                    record.record_link = url;
                    record.record_audio = new Audio(url);
                    this.overlay = false;
                    return true;
                }
            } catch (error) {
                this.showSnackBar("שגיאה בטעינת הקלטה: " + error, "error");
                this.overlay = false;
                return false;
            }
        },

        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color === "error" ? "red" : "green";
            this.snackbar = true;
        },

        async play(item, lang) {
            this.systemRecords.forEach(record => {
                if (record.playingLanguage && record !== item) {
                    this.stop(record);
                }
            });

            const success = await this.setAudio(item, lang);

            if (success && item.record_audio) {
                item.playingLanguage = lang || 'custom';
                item.record_audio.play();

                item.record_audio.addEventListener('ended', () => {
                    this.stop(item);
                });

                item.record_audio.addEventListener('error', () => {
                    this.stop(item);
                    this.showSnackBar('שגיאה: קובץ שמע לא תקין', "error");
                });
            }
        },

        stop(item) {
            if (item.record_audio) {
                item.record_audio.pause();
                item.record_audio.currentTime = 0;
                item.playingLanguage = null;
            }
        },

        triggerFileInput(index) {
            this.$nextTick(() => {
                document.getElementById('fileInput' + index).click();
            });
        },

        async uploadFile(item, event) {
            const file = event.target.files[0];
            if (!file) return;

            try {
                this.loadingText = "מעלה קובץ...";
                this.overlay = true;

                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + `/settings/change_system_record`;

                const formData = new FormData();
                formData.append("record", file);
                formData.append("recordName", item.fileName);

                const response = await axios.post(api, formData, {
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'Content-Type': 'multipart/form-data'
                    }
                });

                if (response.status >= 400) {
                    this.showSnackBar("שגיאה: לא ניתן להעלות את הקובץ", "error");
                } else if (response.status === 200) {
                    this.showSnackBar("הקובץ הועלה בהצלחה!", "success");
                    const index = this.systemRecords.findIndex(record => record.id === item.id);
                    this.systemRecords[index].costumeExist = true;
                }
            } catch (error) {
                this.showSnackBar("שגיאה בהעלאת קובץ: " + error, "error");
            } finally {
                this.overlay = false;
                event.target.value = '';
            }
        },

        confirmDelete(item) {
            this.itemToDelete = item;
            this.deleteDialog = true;
        },

        async deleteConfirmed() {
            if (!this.itemToDelete) return;

            try {
                this.deleteDialog = false;
                this.loadingText = "מוחק הקלטה...";
                this.overlay = true;

                let token = localStorage.getItem("token");
                const recordJSON = JSON.stringify({ item: this.itemToDelete });
                let api = process.env.VUE_APP_BASE_URL + `/settings/delete_system_records`;

                const res = await fetch(api, ApiServices.requestOptions("DELETE", recordJSON, token));

                if (res.status >= 400) {
                    this.showSnackBar("שגיאה במחיקת הקובץ", "error");
                } else if (res.status === 200) {
                    this.showSnackBar("ההקלטה נמחקה בהצלחה!", "success");
                    const index = this.systemRecords.findIndex(record => record.id === this.itemToDelete.id);
                    this.systemRecords[index].costumeExist = false;
                }
            } catch (error) {
                this.showSnackBar("שגיאה במחיקת קובץ: " + error, "error");
            } finally {
                this.overlay = false;
                this.itemToDelete = null;
            }
        },

        refreshDetails() {
            this.getSystemRecords();
        }
    },
    mounted() {
        this.lineNumber = Auth.getLineNumber();
        this.getSystemRecords();
    },
    beforeDestroy() {
        this.systemRecords.forEach(item => {
            if (item.record_audio) {
                item.record_audio.pause();
                item.record_audio = null;
            }
        });
    }
}
</script>

<style scoped>
.audio-settings {
    padding: 2rem 2.5rem;
    background: linear-gradient(145deg, #f8fafc 0%, #eef2f7 100%);
    border-radius: 16px;
    min-height: 400px;
}

/* ===== Header ===== */
.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 1rem;
}

.title-area {
    display: flex;
    align-items: center;
    gap: 10px;
}

.title-icon {
    opacity: 0.8;
}

.section-title {
    color: #0d2c6d;
    font-family: 'Heebo', sans-serif;
    font-size: 1.4rem;
    font-weight: 700;
    margin: 0;
    letter-spacing: -0.3px;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

.records-count {
    font-family: 'Heebo', sans-serif !important;
    font-size: 12px !important;
    font-weight: 500 !important;
}

.search-container {
    width: 260px;
    max-width: 100%;
}

.search-field {
    border-radius: 10px !important;
}

/* ===== Table Container ===== */
.table-container {
    background-color: #fff;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 6px 16px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.04);
}

.audio-table {
    border-radius: 14px;
}

/* ===== Table Headers ===== */
.audio-table>>>th {
    background: linear-gradient(180deg, #f4f6fa 0%, #ebeef5 100%) !important;
    height: 44px !important;
    text-align: center !important;
    border-bottom: 2px solid #dde2ea !important;
}

.table-header-text {
    font-family: 'Heebo', sans-serif !important;
    font-size: 13px;
    font-weight: 700 !important;
    color: #0d2c6d;
}

/* ===== Table Rows ===== */
.table-row {
    transition: all 0.15s ease;
    height: 48px !important;
    border-bottom: 1px solid #f0f2f5;
}

.table-row:hover {
    background-color: #f0f4ff !important;
}

.table-row.row-playing {
    background-color: #fff5f5 !important;
    box-shadow: inset 3px 0 0 #e53935;
}

.audio-table>>>tbody tr:nth-child(even) {
    background-color: #fafbfd;
}

/* ===== File Name ===== */
.td-filename {
    font-family: 'Heebo', sans-serif;
    font-size: 15px;
    font-weight: 600;
}

.filename-chip {
    font-family: 'Courier New', monospace !important;
    font-size: 11.5px !important;
    font-weight: 600 !important;
    letter-spacing: 0.3px;
    border-width: 1.5px !important;
}

/* ===== Audio Play Buttons ===== */
.td-audio {
    padding: 4px 8px !important;
}

.play-btn {
    transition: all 0.2s ease !important;
}

.play-btn:hover {
    transform: scale(1.18);
}

/* ===== Custom Column ===== */
.td-custom {
    padding: 4px 8px !important;
}

.custom-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
}

.action-btn {
    transition: all 0.2s ease !important;
    opacity: 0.65;
}

.action-btn:hover {
    opacity: 1;
    transform: scale(1.18);
}

/* ===== Content Column ===== */
.td-content {
    font-family: 'Heebo', sans-serif !important;
    font-size: 13px !important;
    color: #47484b;
    text-align: center !important;
    max-width: 200px;
}

.file-content-wrapper {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    cursor: default;
    padding: 3px 10px;
    border-radius: 8px;
    transition: all 0.2s ease;
    max-width: 100%;
}

.file-content-wrapper:hover {
    background-color: #edf1f8;
}

.file-content-text {
    font-family: 'Heebo' !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 150px;
    display: inline-block;
    font-size: 16px;
    color: #555;
}

.expand-icon {
    flex-shrink: 0;
    opacity: 0.5;
    color: #0d2c6d !important;
    transition: opacity 0.2s;
}

.file-content-wrapper:hover .expand-icon {
    opacity: 0.8;
}

.no-content {
    color: #d0d0d0;
    font-size: 14px;
}

/* ===== Empty State ===== */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 1rem;
}

.empty-icon-wrapper {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #f1f4f9 0%, #e8ecf4 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
}

.empty-title {
    font-family: 'Heebo', sans-serif;
    font-size: 1.1rem;
    font-weight: 600;
    color: #555;
    margin: 0 0 4px 0;
}

.empty-subtitle {
    font-family: 'Heebo', sans-serif;
    font-size: 0.85rem;
    color: #999;
    margin: 0;
}

/* ===== Loading ===== */
.loading-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: white;
    border-radius: 16px;
    padding: 2rem 2.5rem;
}

.loading-text {
    font-family: 'Heebo', sans-serif;
    font-size: 0.9rem;
    color: #555;
    font-weight: 500;
}

/* ===== Delete Dialog ===== */
.delete-card {
    border-radius: 14px !important;
    overflow: hidden;
}

.delete-title {
    font-family: 'Heebo', sans-serif !important;
    font-size: 1.1rem !important;
    font-weight: 600 !important;
    color: #333;
    padding-bottom: 8px !important;
}

.delete-text {
    font-family: 'Heebo', sans-serif !important;
    font-size: 14px !important;
    color: #666;
    line-height: 1.6 !important;
}

.delete-actions {
    padding: 8px 16px 16px !important;
}

.action-cancel {
    font-family: 'Heebo', sans-serif !important;
}

/* ===== Snackbar ===== */
.snackbar-content {
    display: flex;
    align-items: center;
    font-family: 'Heebo', sans-serif;
    font-size: 14px;
}

/* ===== Footer ===== */
.audio-table>>>.v-data-footer {
    font-family: 'Heebo', sans-serif !important;
    border-top: 2px solid #f0f2f5;
}

/* ===== Responsive ===== */
@media (max-width: 960px) {
    .header-section {
        flex-direction: column;
        align-items: flex-start;
    }

    .header-actions {
        width: 100%;
        flex-wrap: wrap;
    }

    .search-container {
        width: 100%;
    }
}

@media (max-width: 600px) {
    .audio-settings {
        padding: 0.75rem;
        border-radius: 10px;
    }

    .section-title {
        font-size: 1.15rem;
    }
}
</style>

<style>
/* Tooltip styles — unscoped because Vuetify renders tooltips outside the component */
.content-tooltip {
    background: linear-gradient(135deg, #1a2a4a 0%, #0d2c6d 100%) !important;
    color: #fff !important;
    font-family: 'Heebo', sans-serif !important;
    font-size: 14px !important;
    line-height: 1.8 !important;
    padding: 14px 18px !important;
    border-radius: 10px !important;
    box-shadow: 0 8px 30px rgba(13, 44, 109, 0.3) !important;
    white-space: pre-wrap !important;
    word-break: break-word !important;
    text-align: right !important;
    direction: rtl !important;
    opacity: 1 !important;
}

.content-tooltip .tooltip-content {
    max-height: 300px;
    overflow-y: auto;
}

.action-tooltip {
    background-color: #37474f !important;
    font-family: 'Heebo', sans-serif !important;
    font-size: 12px !important;
    border-radius: 6px !important;
    padding: 4px 10px !important;
}
</style>